import { getBackendEndpoint } from '../utils/backendApiUrl';

class ReportDraftApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'ReportDraftApiError';
    this.code = options.code || 'draft_api_error';
    this.status = Number.isFinite(Number(options.status)) ? Number(options.status) : null;
    this.data = options.data || null;
    this.cause = options.cause || null;
  }
}

const buildDraftEndpoint = (reportId, locale) => (
  getBackendEndpoint(`/reports/${encodeURIComponent(reportId)}/drafts/${encodeURIComponent(locale)}`)
);

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const mapApiDraft = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  if (typeof payload.editorState !== 'string') {
    return null;
  }
  return {
    reportId: typeof payload.reportId === 'string' ? payload.reportId : null,
    locale: typeof payload.locale === 'string' ? payload.locale : null,
    templateVersion:
      typeof payload.templateVersion === 'string' && payload.templateVersion.trim()
        ? payload.templateVersion.trim()
        : null,
    editorState: payload.editorState,
    serverRevision: Number.isFinite(Number(payload.serverRevision))
      ? Number(payload.serverRevision)
      : 1,
    updatedAt:
      typeof payload.updatedAt === 'string' && payload.updatedAt.trim()
        ? payload.updatedAt
        : new Date().toISOString(),
    updatedBy:
      typeof payload.updatedBy === 'string' && payload.updatedBy.trim()
        ? payload.updatedBy
        : null,
    stateHash:
      typeof payload.stateHash === 'string' && payload.stateHash.trim()
        ? payload.stateHash
        : null,
    source: 'server',
  };
};

const requestDraftApi = async (url, init = {}) => {
  let response;
  try {
    response = await fetch(url, {
      credentials: 'same-origin',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (error) {
    throw new ReportDraftApiError('Network error while syncing draft.', {
      code: 'network_error',
      cause: error,
    });
  }

  if (response.status === 204) {
    return { ok: true, status: 204, data: null };
  }

  const data = await parseJsonSafely(response);

  if (!response.ok) {
    const normalizedErrorCode = typeof data?.error === 'string' ? data.error : null;
    const method = String(init.method || 'GET').toUpperCase();
    const defaultMessage = (() => {
      if (response.status === 404 && method !== 'GET') {
        return 'Draft API endpoint not found (404). Check backend deployment/proxy and REACT_APP_BACKEND_URL.';
      }
      return `Draft API request failed (${response.status})`;
    })();
    throw new ReportDraftApiError(
      typeof data?.message === 'string' ? data.message : defaultMessage,
      {
        code:
          normalizedErrorCode === 'conflict'
            ? 'conflict'
            : (normalizedErrorCode || 'http_error'),
        status: response.status,
        data,
      }
    );
  }

  return { ok: true, status: response.status, data };
};

export const getReportDraftFromServer = async ({ reportId, locale, signal } = {}) => {
  if (!reportId || !locale) {
    return null;
  }
  try {
    const response = await requestDraftApi(buildDraftEndpoint(reportId, locale), {
      method: 'GET',
      signal,
    });
    return mapApiDraft(response.data);
  } catch (error) {
    if (error instanceof ReportDraftApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
};

export const putReportDraftToServer = async ({
  reportId,
  locale,
  payload,
  baseServerRevision = null,
  force = false,
  signal,
} = {}) => {
  if (!reportId || !locale) {
    throw new ReportDraftApiError('reportId and locale are required.', { code: 'invalid_request' });
  }
  if (!payload || typeof payload !== 'object') {
    throw new ReportDraftApiError('payload is required.', { code: 'invalid_request' });
  }

  const body = {
    templateVersion: payload.templateVersion || null,
    editorState: payload.editorState,
    clientInstanceId: payload.clientInstanceId || null,
    clientRevision: Number.isFinite(Number(payload.clientRevision))
      ? Number(payload.clientRevision)
      : null,
    baseServerRevision: Number.isFinite(Number(baseServerRevision))
      ? Number(baseServerRevision)
      : null,
    localSavedAt: payload.timestamp || null,
    force: force === true,
  };

  try {
    const response = await requestDraftApi(buildDraftEndpoint(reportId, locale), {
      method: 'PUT',
      body: JSON.stringify(body),
      signal,
    });
    return mapApiDraft(response.data);
  } catch (error) {
    if (error instanceof ReportDraftApiError && error.code === 'conflict') {
      const serverDraft = mapApiDraft(error.data?.serverDraft || null);
      error.serverDraft = serverDraft;
    }
    throw error;
  }
};

export const deleteReportDraftOnServer = async ({ reportId, locale, signal } = {}) => {
  if (!reportId || !locale) {
    return;
  }
  await requestDraftApi(buildDraftEndpoint(reportId, locale), {
    method: 'DELETE',
    signal,
  });
};

export {
  ReportDraftApiError,
  mapApiDraft,
};
